/**
 * @attr {string} timeStep - the step for minutes (when increasing/decreasing)
 * @attr {string} defaultValue -  default time (for ex. 12:00)
 * @attr {string} timeSeparator -  symbol for separating hours and minutes
 *
 * <e2-timepicker
 *      timeStep="5"
 *      defaultValue="12:00"
 *      timeSeparator=":"
 *      >
 * </e2-timepicker>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { ifDefined } from 'lit-html/directives/if-defined';

import connect from '@elab/core/store/connect';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import { timePickerConstants } from '@elab/core/utils/constants';
import { cacheSelector, deferred } from '@elab/core/utils/webComponent';

import { FormElement } from './Form';

const {
    HOUR_STEP,
    MINUTE_STEP,
    MAX_HOUR_VALUE,
    MAX_MINUTE_VALUE,
    MIN_FIELD_VALUE,
    TIME_SEPARATOR,
    FORMAT_PANELS,
} = timePickerConstants;

export class TimePicker extends FormElement {
    static get properties() {
        return {
            timeStep: { type: Number, attribute: 'time-step' },
            defaultValue: { type: String, attribute: 'default-value' },
            timeSeparator: { type: String, attribute: 'time-separator' },
            required: { type: Boolean, reflect: true },
            hourStep: { type: Number, attribute: false },
            minuteStep: { type: Number, attribute: false },
            hours: { type: String, attribute: false },
            minutes: { type: String, attribute: false },
            isOpen: { type: Boolean, attribute: false },
            isFocused: { type: Boolean, attribute: false },
            readonlyField: { type: Boolean, attribute: false },
            gtmLabel: {
                type: String,
                attribute: 'gtm-label',
            },
        };
    }

    constructor() {
        super();
        this.hourStep = HOUR_STEP;
        this.minuteStep = this.timeStep || MINUTE_STEP;
        this.isOpen = false;
        this.isFocused = false;
        this.defaultValue = '';
        this.timeFormat = Object.values(FORMAT_PANELS);
        this.timeSeparator = TIME_SEPARATOR;
        this.readonlyField = true;
        this.hours = '';
        this.minutes = '';
        this.gtmLabel = '';
    }

    firstUpdated() {
        const time = this.defaultValue.split(this.timeSeparator);
        this.hours = this.formatTime(time[0]);
        this.minutes = this.formatTime(time[1]);
    }

    formatTime(value) {
        return `${value}`.padStart(2, '0');
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);

        if (_changedProperties.has('isFocused')) {
            this.setStatus(this.isFocused);
        }

        if (_changedProperties.has('minutes') || _changedProperties.has('hours')) {
            if (this.hours && this.minutes) {
                const hours = this.formatTime(this.hours);
                const minutes = this.formatTime(this.minutes);
                const value = `${hours}${this.timeSeparator}${minutes}`;

                this.changeValue(value);
            }
        }
    }

    setStatus(status) {
        this.isOpen = status;
    }

    increaseValue(fieldType) {
        switch (fieldType) {
            case FORMAT_PANELS.HOURS: {
                const resultHours = +this.hours + this.hourStep;
                if (resultHours > MAX_HOUR_VALUE) return;
                this.hours = this.formatTime(resultHours);
                break;
            }
            case FORMAT_PANELS.MINUTES: {
                const resultMinutes = +this.minutes + this.minuteStep;
                if (resultMinutes > MAX_MINUTE_VALUE) return;
                this.minutes = this.formatTime(resultMinutes);
                break;
            }
            default:
                return null;
        }
    }

    decreaseValue(fieldType) {
        switch (fieldType) {
            case FORMAT_PANELS.HOURS: {
                if (parseInt(this.hours) === MIN_FIELD_VALUE) return;
                const resultHours = this.hours - this.hourStep;
                this.hours = this.formatTime(resultHours);
                break;
            }
            case FORMAT_PANELS.MINUTES: {
                if (parseInt(this.minutes) === MIN_FIELD_VALUE || this.minutes < this.minuteStep)
                    return;
                const resultMinutes = this.minutes - this.minuteStep;
                this.minutes = this.formatTime(resultMinutes);
                break;
            }
            default:
                return null;
        }
    }

    findFieldValue(fieldType) {
        switch (fieldType) {
            case FORMAT_PANELS.HOURS:
                return this.hours;
            case FORMAT_PANELS.MINUTES:
                return this.minutes;
            default:
                return null;
        }
    }

    timeFieldTemplate = fieldType => {
        const value = this.findFieldValue(fieldType);

        return html`
            <div class="timepicker__field-wrapper">
                <input
                    class="timepicker__field"
                    type="number"
                    .value="${value}"
                    @input="${e => this.handleInput(e, fieldType)}"
                    @focus="${() => this.handleFocusSwitch(true)}"
                    @blur=${() => this.handleFocusSwitch(false)}
                    data-gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                />
                <div class="timepicker__arrows">
                    <span
                        class="timepicker__arrow-up"
                        @click="${() => this.increaseValue(fieldType)}"
                    ></span>
                    <span
                        class="timepicker__arrow-down"
                        @click="${() => this.decreaseValue(fieldType)}"
                    ></span>
                </div>
            </div>
        `;
    };

    validateTime(value, maxValue, currentTime) {
        let absValue = Math.abs(value);

        if (absValue > maxValue) {
            while (absValue > maxValue) {
                absValue -= maxValue;
            }

            return absValue;
        }
        if (!absValue) {
            return currentTime;
        }

        return absValue;
    }

    handleInput(ev, fieldType) {
        const { value } = ev.target;

        switch (fieldType) {
            case FORMAT_PANELS.HOURS:
                this.hours = this.validateTime(value, MAX_HOUR_VALUE, this.hours);
                break;
            case FORMAT_PANELS.MINUTES:
                this.minutes = this.validateTime(value, MAX_MINUTE_VALUE, this.minutes);
                break;
            default:
                return null;
        }
    }

    handleFocusSwitch = deferred(focused => {
        const event = focused ? 'FOCUS-FIELD' : 'BLUR-FIELD';
        this.dispatchEvent(
            new CustomEvent(event, {
                detail: { fieldName: this.fieldValue.name },
            }),
        );
    }, 100);

    innerContainer() {
        return html`
            <label
                tabindex="1"
                class="timepicker__wrapper"
                @click="${() => this.setStatus(true)}"
                @focus=${() => this.handleFocusSwitch(true)}
                @blur=${() => this.handleFocusSwitch(false)}
            >
                <e2-input-field
                    name="${this.fieldValue.name}"
                    store-name="${this.storeName}"
                    readonly="${this.readonlyField}"
                    disabled
                    hidden-focus-label
                    show-disabled-as-normal
                    gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                >
                    <div v-slot="error">
                        ${this.fieldValue.error}
                    </div>
                </e2-input-field>
                <div class="timepicker__panel">
                    ${this.timeFormat.map(this.timeFieldTemplate)}
                    <span class="timepicker__time-separator">${this.timeSeparator}</span>
                </div>
            </label>
        `;
    }

    render() {
        if (this.fieldValue) {
            const timepickerModifiers = {
                timepicker: true,
                'timepicker--opened': this.isOpen,
            };

            return html`
                <div class="${classMap(timepickerModifiers)}">
                    <v-slot name="label"></v-slot>
                    ${this.innerContainer()}
                </div>
            `;
        }
    }
}

const mapStateToProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);
    return {
        required: selectors.isDataFieldRequired(state, ctx.name),
        isFocused: selectors.isDataFieldFocused(state, ctx.name),
    };
};

export default () => customElements.define('e2-timepicker', connect(mapStateToProps)(TimePicker));
